import {ManagingResources} from "../common/lifetime";
import {autoRegister, resolve} from "../container";
import {prepareEvents} from "../common/utils/events";
import {GLOBAL} from "../common/globals";
import {Tracking} from "./tracking";
import {type GenericTrackingEventPayload, type NavigationTrackingEventPayload, TrackingEventLocation, TrackingEventName, TrackingEventType} from "./types";
import {TrackingEventFactories} from "./trackingEventFactory";

@autoRegister()
export class ClickTracking {

    public constructor(
        private tracking: Tracking = resolve(Tracking),
        private trackingEventFactory: TrackingEventFactories = resolve(TrackingEventFactories)
    ) {
    }

    public handleTextlinkInteract(event: Event, text: string, link?: string): void {
        const textlinkEvent = this.trackingEventFactory.createFrom(event, TrackingEventName.TEXTLINK_INTERACT, {
            text: text,
            link: link
        });
        this.tracking.emitTrackingEvent(textlinkEvent);
    }

    public handleButtonInteract(event: Event, text: string): void {
        const buttonEvent = this.trackingEventFactory.createFrom(event, TrackingEventName.BUTTON_INTERACT, {text: text});
        this.tracking.emitTrackingEvent(buttonEvent);
    }

    public handleNavigationInteract(event: Event, text: string, type: TrackingEventType = TrackingEventType.CLICK, link?: string): void {
        const navigationEvent = this.trackingEventFactory.createFrom(event, TrackingEventName.NAVIGATION_INTERACT, {
            text: text,
            type: type,
            link: link
        });
        this.tracking.emitNavigationTrackingEvent(navigationEvent);
    }

    public handleDislocatedNavigationInteract(
        event: Event,
        text: string,
        location: TrackingEventLocation,
        contentId: string,
        type: TrackingEventType = TrackingEventType.CLICK,
        link?: string
    ): void {
        const navigationEvent = this.trackingEventFactory.createFrom(event, TrackingEventName.NAVIGATION_INTERACT, {
            text: text,
            type: type,
            link: link,
            location: location,
            contentId: contentId
        });
        this.tracking.emitNavigationTrackingEvent(navigationEvent);
    }

    public handleBodyClick(event: MouseEvent): void {
        const trackable = this.trackingEventFactory.createFromAttributes(event);
        if (trackable) {
            if (trackable.name === TrackingEventName.NAVIGATION_INTERACT) {
                return this.tracking.emitNavigationTrackingEvent({
                    name: trackable.name,
                    data: trackable.data as NavigationTrackingEventPayload
                });
            }
            return this.tracking.emitTrackingEvent({
                name: trackable.name,
                data: trackable.data as GenericTrackingEventPayload
            });
        }
    }
}

export class EopClickTracking extends ManagingResources(HTMLElement) {

    public constructor(private clickTrackingListener: ClickTracking = resolve(ClickTracking)) {
        super();
    }

    public connectedCallback(): void {
        prepareEvents(GLOBAL.bodyElement())
            .boundTo(this)
            .on("click", ev => this.clickTrackingListener.handleBodyClick(ev as MouseEvent));
    }
}

customElements.define("eop-click-tracking", EopClickTracking);